import { useEffect, useRef, type ReactNode } from 'react'

/**
 * Modal accesible reutilizable: overlay oscurecido + caja centrada.
 *
 * - Cierra con Escape, clic en el fondo, o el botón ✕ (si se usa la cabecera).
 * - Atrapa el foco dentro mientras está abierto y lo devuelve al cerrar.
 * - Bloquea el scroll del body: el contenido scrollea DENTRO del modal, no la
 *   página (que es justo lo que evita la mala experiencia del panel lateral).
 *
 * Es presentacional: el contenido y el ancho los decide quien lo usa.
 */

interface Props {
  /** Etiqueta accesible del diálogo. */
  titulo: string
  /** Cerrar el modal (Escape / clic fondo / ✕). */
  onCerrar: () => void
  /** Ancho máximo (clase Tailwind), p. ej. 'max-w-2xl'. */
  anchoMax?: string
  children: ReactNode
}

export function Modal({ titulo, onCerrar, anchoMax = 'max-w-xl', children }: Props) {
  const cajaRef = useRef<HTMLDivElement>(null)

  // Bloquea el scroll del body y enfoca la caja SOLO al montar (una vez). Si
  // dependiera de onCerrar —que suele ser una función nueva por render—, el
  // focus se repetiría en cada render y robaría el foco a los inputs internos.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    cajaRef.current?.focus()
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  // Escape cierra. Se re-suscribe si cambia onCerrar, pero sin tocar el foco.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onCerrar])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      // Clic en el fondo (no en la caja) cierra.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCerrar()
      }}
    >
      {/* Fondo oscurecido. Lleva su propio cierre: cubre todo el contenedor, así
          que sin esto un clic en la zona oscura (que es lo normal) golpearía este
          div y NO el contenedor, y el modal no se cerraría. */}
      <div
        aria-hidden
        className="absolute inset-0 bg-[rgba(3,9,17,0.68)] backdrop-blur-[2px]"
        onMouseDown={onCerrar}
      />

      {/* Caja */}
      <div
        ref={cajaRef}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        tabIndex={-1}
        className={[
          'relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl border border-borde bg-superficie shadow-2xl outline-none',
          anchoMax,
        ].join(' ')}
      >
        {children}
      </div>
    </div>
  )
}
